import {
  BarList,
  DataTable,
  EmptyState,
  formatCount,
  formatDate,
  formatNumber,
  formatPercent,
  LoadingState,
  PageLayout,
  relativeChange,
  SearchInput,
  SectionCard,
  SegmentedControl,
  TimeSeriesChart,
  TrendBadge,
  truncateMiddle,
} from '@repo/ui'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { BuildingIcon, CoinsIcon, CpuIcon, SparklesIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  createMoneyFormatter,
  type Currency,
} from '@/components/ai-spend/currency'
import { sourceLabel } from '@/components/ai-spend/palette'
import { SourceBreakdown } from '@/components/ai-spend/source-breakdown'
import { SpendMetric } from '@/components/ai-spend/spend-metrics'
import { ErrorState } from '@/components/error-state'
import { StatusBadge } from '@/components/status-badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAiSpend } from '@/hooks/use-ai-spend'
import type { AISpendOrgRow } from '@/lib/api-types'

export const Route = createFileRoute('/_app/gastos')({
  component: SpendPage,
})

const PERIOD_PRESETS = [
  { value: '7', label: '7 dias' },
  { value: '30', label: '30 dias' },
  { value: '90', label: '90 dias' },
  { value: '365', label: '12 meses' },
] as const

const ALL = '__all__'
const TOP_ORGS_IN_CHART = 10

function SpendPage() {
  const navigate = useNavigate()
  const [days, setDays] = useState<number>(30)
  const [source, setSource] = useState<string>(ALL)
  const [model, setModel] = useState<string>(ALL)
  const [currency, setCurrency] = useState<Currency>('BRL')
  const [search, setSearch] = useState('')

  const spend = useAiSpend({
    days,
    source: source === ALL ? '' : source,
    model: model === ALL ? '' : model,
    limit: 200,
  })

  const money = createMoneyFormatter(currency, spend.data?.exchangeRate)

  const orgRows = useMemo(() => {
    const rows = spend.data?.byOrg ?? []
    const normalized = search.trim().toLowerCase()
    if (!normalized) return rows
    return rows.filter((row) =>
      [row.name, row.slug, row.organizationId]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(normalized))
    )
  }, [spend.data?.byOrg, search])

  const layout = {
    icon: CoinsIcon,
    title: 'Gastos com IA',
    description:
      'Consumo de tokens de todas as organizações, com custo estimado por período.',
  }

  if (spend.isError) {
    return (
      <PageLayout {...layout}>
        <ErrorState onRetry={() => void spend.refetch()} />
      </PageLayout>
    )
  }

  const data = spend.data
  const rate = data?.exchangeRate

  return (
    <PageLayout {...layout} contentClassName="space-y-5">
      {/* Uma linha de filtros acima de tudo: cartões, gráficos e tabelas leem a mesma fatia. */}
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl
          label="Período"
          options={PERIOD_PRESETS}
          value={String(days)}
          onChange={(value) => setDays(Number(value))}
        />
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger aria-label="Origem" className="w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todas as origens</SelectItem>
            {(data?.availableSources ?? []).map((value) => (
              <SelectItem key={value} value={value}>
                {sourceLabel(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={model} onValueChange={setModel}>
          <SelectTrigger aria-label="Modelo" className="w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os modelos</SelectItem>
            {(data?.availableModels ?? []).map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <SegmentedControl
          label="Moeda"
          className="ml-auto"
          options={[
            { value: 'BRL', label: 'BRL', disabled: !rate?.rate },
            { value: 'USD', label: 'USD' },
          ]}
          value={money.currency}
          onChange={(value) => setCurrency(value as Currency)}
        />
      </div>

      {/* Refetch segura o quadro anterior esmaecido: sem esqueleto, sem pulo de layout. */}
      <div
        className={
          spend.isFetching
            ? 'space-y-5 opacity-60 transition-opacity'
            : 'space-y-5'
        }
      >
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SpendMetric
            label="Custo no período"
            value={money.format(data?.totals.costUsd ?? 0)}
            hint={money.formatAlternate(data?.totals.costUsd ?? 0) ?? undefined}
            current={data?.totals.costUsd}
            previous={data?.previousTotals.costUsd}
            icon={CoinsIcon}
          />
          <SpendMetric
            label="Tokens consumidos"
            value={formatNumber(data?.totals.totalTokens ?? 0)}
            hint={`${formatNumber(data?.totals.cachedInputTokens ?? 0)} em cache`}
            current={data?.totals.totalTokens}
            previous={data?.previousTotals.totalTokens}
            icon={SparklesIcon}
          />
          <SpendMetric
            label="Requisições"
            value={formatNumber(data?.totals.requests ?? 0)}
            hint={
              data && data.totals.unpricedRequests > 0
                ? `${formatNumber(data.totals.unpricedRequests)} sem preço na tabela`
                : undefined
            }
            current={data?.totals.requests}
            previous={data?.previousTotals.requests}
            icon={CpuIcon}
          />
          <SpendMetric
            label="Organizações com consumo"
            value={formatNumber(data?.totals.organizations ?? 0)}
            hint={
              data
                ? `média ${money.format(
                    data.totals.organizations > 0
                      ? data.totals.costUsd / data.totals.organizations
                      : 0
                  )} por org`
                : undefined
            }
            current={data?.totals.organizations}
            previous={data?.previousTotals.organizations}
            icon={BuildingIcon}
          />
        </div>

        <SectionCard
          title={`Custo por dia (${money.currency})`}
          description="Estimativa diária no período selecionado."
        >
          {data ? (
            <TimeSeriesChart
              data={data.byDay}
              series={[{ key: 'costUsd', label: 'Custo' }]}
              formatValue={money.format}
              formatAxisValue={money.formatCompact}
              renderDetail={(point) =>
                `${formatNumber(point.requests)} req · ${formatNumber(point.totalTokens)} tokens`
              }
            />
          ) : (
            <LoadingState className="h-72" />
          )}
        </SectionCard>

        <div className="grid items-start gap-5 xl:grid-cols-2">
          <SectionCard
            title="Organizações que mais gastaram"
            description={`As ${TOP_ORGS_IN_CHART} maiores no período.`}
          >
            {!data || data.byOrg.length === 0 ? (
              <EmptyState
                variant="inline"
                icon={CoinsIcon}
                title="Sem consumo de IA no período"
              />
            ) : (
              <BarList
                items={data.byOrg.slice(0, TOP_ORGS_IN_CHART).map((row) => ({
                  key: row.organizationId,
                  label: (
                    <Link
                      to="/organizacoes/$orgId"
                      params={{ orgId: row.organizationId }}
                      className="hover:underline"
                    >
                      {row.name}
                    </Link>
                  ),
                  value: row.costUsd,
                  display: money.format(row.costUsd),
                  hint: `${formatPercent(row.share)} do período · ${formatNumber(row.requests)} req`,
                }))}
              />
            )}
          </SectionCard>

          <SectionCard
            title="Custo por origem"
            description="Onde a IA foi usada: agente, otimizador, testes..."
          >
            {data ? (
              <SourceBreakdown rows={data.bySource} money={money} />
            ) : (
              <LoadingState className="h-72" />
            )}
          </SectionCard>
        </div>

        <SectionCard
          title="Gasto por organização"
          description={`${formatCount(orgRows.length, 'organização', 'organizações')} com consumo`}
          actions={
            <SearchInput
              className="w-72"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filtrar por nome, slug ou ID"
            />
          }
          contentClassName="p-0"
        >
          <DataTable
            variant="flush"
            rows={orgRows}
            getRowKey={(row) => row.organizationId}
            loading={spend.isLoading}
            empty="Nenhuma organização com consumo no período."
            pageSize={25}
            defaultSort={{ key: 'cost', direction: 'desc' }}
            onRowClick={(row) =>
              void navigate({
                to: '/organizacoes/$orgId',
                params: { orgId: row.organizationId },
              })
            }
            columns={[
              {
                key: 'org',
                header: 'Organização',
                sortValue: (row) => row.name,
                render: (row) => (
                  <Link
                    to="/organizacoes/$orgId"
                    params={{ orgId: row.organizationId }}
                    className="block hover:underline"
                  >
                    <span className="block font-medium">{row.name}</span>
                    <span className="text-muted-foreground block text-xs">
                      {row.slug ?? truncateMiddle(row.organizationId)}
                    </span>
                  </Link>
                ),
              },
              {
                key: 'plan',
                header: 'Plano',
                render: (row) => <StatusBadge value={row.plan} />,
              },
              {
                key: 'cost',
                header: 'Custo',
                align: 'right',
                sortValue: (row) => row.costUsd,
                render: (row) => (
                  <div>
                    <p className="font-medium">{money.format(row.costUsd)}</p>
                    <p className="text-muted-foreground text-xs">
                      {money.formatAlternate(row.costUsd) ?? '—'}
                    </p>
                  </div>
                ),
              },
              {
                key: 'share',
                header: '% do total',
                align: 'right',
                render: (row) => formatPercent(row.share),
              },
              {
                key: 'trend',
                header: 'vs anterior',
                align: 'right',
                sortValue: (row) =>
                  relativeChange(row.costUsd, row.previousCostUsd),
                render: (row) => <PeriodDelta row={row} />,
              },
              {
                key: 'tokens',
                header: 'Tokens',
                align: 'right',
                sortValue: (row) => row.totalTokens,
                render: (row) => (
                  <div>
                    <p>{formatNumber(row.totalTokens)}</p>
                    <p className="text-muted-foreground text-xs">
                      {formatNumber(row.inputTokens)} ent /{' '}
                      {formatNumber(row.outputTokens)} sai
                    </p>
                  </div>
                ),
              },
              {
                key: 'requests',
                header: 'Reqs',
                align: 'right',
                render: (row) => (
                  <div>
                    <p>{formatNumber(row.requests)}</p>
                    {row.unpricedRequests > 0 ? (
                      <p className="text-muted-foreground text-xs">
                        {formatNumber(row.unpricedRequests)} sem preço
                      </p>
                    ) : null}
                  </div>
                ),
              },
              {
                key: 'last',
                header: 'Último uso',
                sortValue: (row) => row.lastUsedAt,
                render: (row) => formatDate(row.lastUsedAt),
              },
            ]}
          />
        </SectionCard>

        <SectionCard
          title="Custo por modelo"
          description="Tokens de entrada, cache e saída por modelo."
          contentClassName="p-0"
        >
          <DataTable
            variant="flush"
            rows={data?.byModel ?? []}
            getRowKey={(row) => row.key}
            loading={spend.isLoading}
            empty="Sem registros no período."
            defaultSort={{ key: 'cost', direction: 'desc' }}
            columns={[
              {
                key: 'model',
                header: 'Modelo',
                render: (row) => (
                  <span className="font-mono text-xs">{row.key}</span>
                ),
              },
              {
                key: 'cost',
                header: 'Custo',
                align: 'right',
                sortValue: (row) => row.costUsd,
                render: (row) => money.format(row.costUsd),
              },
              {
                key: 'share',
                header: '% do total',
                align: 'right',
                render: (row) => formatPercent(row.share),
              },
              {
                key: 'tokens',
                header: 'Tokens (ent / cache / sai)',
                align: 'right',
                render: (row) =>
                  `${formatNumber(row.inputTokens)} / ${formatNumber(
                    row.cachedInputTokens
                  )} / ${formatNumber(row.outputTokens)}`,
              },
              {
                key: 'requests',
                header: 'Reqs',
                align: 'right',
                render: (row) =>
                  row.unpricedRequests > 0
                    ? `${formatNumber(row.requests)} (${formatNumber(
                        row.unpricedRequests
                      )} sem preço)`
                    : formatNumber(row.requests),
              },
            ]}
          />
        </SectionCard>
      </div>

      {data ? (
        <p className="text-muted-foreground text-xs">
          {data.disclaimer}
          {rate?.rate
            ? ` Cotação USD ${rate.rate.toFixed(4)} BRL via ${rate.source}${
                rate.stale ? ' (desatualizada)' : ''
              }, em ${formatDate(rate.fetchedAt)}.`
            : ' Cotação USD-BRL indisponível: os valores seguem em USD.'}{' '}
          Dias apurados no fuso {data.period.timeZone}.
        </p>
      ) : null}
    </PageLayout>
  )
}

function PeriodDelta({ row }: { row: AISpendOrgRow }) {
  const change = relativeChange(row.costUsd, row.previousCostUsd)
  if (change === null) {
    return <span className="text-muted-foreground text-xs">novo</span>
  }
  return <TrendBadge value={change} goodWhen="neutral" />
}
